import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/AboutUsPage.module.css';
import videoBackground from '../styles/background.mp4';

/* ─── DATA ─────────────────────────────────────────────── */

const MILESTONES = [
  {
    year: "2021",
    title: "The Idea",
    desc: "Two Brothers Freight Solutions identified a critical gap in the trucking market — no single platform handled dispatch, payroll, fuel, invoicing, and compliance in one place.",
  },
  {
    year: "2022",
    title: "First Build",
    desc: "The FTS platform was architected from scratch by professionals with direct trucking experience. Every feature was validated against real dispatcher and driver workflows.",
  },
  {
    year: "2023",
    title: "Beta Launch",
    desc: "FTS entered beta with early adopters across the US and Canada. Load management, driver pays, and IFTA reporting were the first modules to go live.",
  },
  {
    year: "2024",
    title: "AI Integration",
    desc: "AI-powered load entry was introduced — dispatchers could upload a Rate Confirmation and let the system auto-fill every field. Fuel report CSV import and banking dashboards launched.",
  },
  {
    year: "2025",
    title: "Full Platform",
    desc: "FTS reached feature completeness: invoicing, factoring, employee payroll, GPS tracking, mobile driver app, balance sheet, and real-time financial dashboards — all in one platform.",
  },
];

const VALUES = [
  {
    icon: "🎯",
    title: "Built for the Road",
    desc: "FTS was not built in a vacuum. Every feature was shaped by dispatchers, drivers, and fleet managers who live these challenges daily. We solve real problems, not theoretical ones.",
  },
  {
    icon: "⚡",
    title: "Speed is Everything",
    desc: "In trucking, minutes matter. FTS turns hours of manual work into seconds. From AI-powered load entry to one-click invoice generation — we relentlessly optimize for speed.",
  },
  {
    icon: "🔒",
    title: "Accuracy & Compliance",
    desc: "Wrong data costs money and damages trust. FTS automates IFTA reporting, fuel tracking, and financial reconciliation so your numbers are always right and audit-ready.",
  },
  {
    icon: "📈",
    title: "Growth at Every Stage",
    desc: "Whether you're a single owner-operator or a 100-truck enterprise, FTS scales with you. Our subscription tiers and per-driver pricing ensure you only pay for what you use.",
  },
];

const CAPABILITIES = [
  { label: "AI-Powered Load Entry",       desc: "Upload a Rate Confirmation — FTS extracts every field automatically." },
  { label: "Driver Pay Automation",        desc: "Per mile, hourly, fixed, percentage — generate pays in seconds." },
  { label: "Fuel & IFTA Reporting",        desc: "Log fuel, import CSVs, generate quarterly IFTA tax compliance reports." },
  { label: "Invoice Generation & Email",   desc: "Auto-create invoices from completed loads. Send directly to brokers." },
  { label: "Factoring Integration",        desc: "Upload BOL/RC/POD packages and email factoring companies in one click." },
  { label: "Real-Time Financial Dashboard",desc: "Live KPIs: revenue, expenses, open invoices, and profit margins." },
  { label: "Balance Sheet",               desc: "See every dollar in and out — assets, liabilities, income, expenses." },
  { label: "Employee Payroll",             desc: "Full payroll for dispatchers, accountants, and office staff." },
  { label: "Fleet & Trailer Management",  desc: "Track insurance expiry, maintenance, leasing, and IFTA per asset." },
  { label: "Mobile Driver App",            desc: "GPS, document scanner, broker secure links, dispatcher chat." },
  { label: "Banking Integration",          desc: "Connect your bank account for live transactions and direct payments." },
  { label: "Received Invoice Archiving",  desc: "Record, edit, and mark vendor invoices as paid with full history." },
];

const TEAM_VALUES = [
  "We ship features dispatchers actually asked for",
  "We test against real trucking workflows, not use cases",
  "We respond to customer feedback within 24 hours",
  "We build for reliability — 99.8% uptime, always",
];

/* ─── COMPONENT ─────────────────────────────────────────── */

const AboutUsPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visibleMilestone, setVisibleMilestone] = useState(-1);
  const timelineRef = useRef(null);
  const [capFilter, setCapFilter] = useState(null);

  /* Staggered timeline reveal */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          MILESTONES.forEach((_, i) => {
            setTimeout(() => setVisibleMilestone(i), i * 180);
          });
        }
      },
      { threshold: 0.2 }
    );
    if (timelineRef.current) observer.observe(timelineRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.aboutRoot}>
      {/* Video BG */}
      <video className={styles.videoBg} autoPlay loop muted playsInline>
        <source src={videoBackground} type="video/mp4" />
      </video>
      <div className={styles.videoOverlay} />

      {/* ── HEADER ── */}
      <header className={styles.header}>
        <div className={styles.logo} onClick={() => navigate('/')}>
          Full Trucking Service
        </div>

        <div
          className={styles.hamburger}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? '✕' : '☰'}
        </div>

        <nav className={`${styles.nav} ${isMenuOpen ? styles.navOpen : ''}`}>
          <ul>
            <li onClick={() => { navigate('/about'); setIsMenuOpen(false); }}>About Us</li>
            <li onClick={() => { navigate('/services'); setIsMenuOpen(false); }}>Services</li>
            <li onClick={() => { navigate('/contact'); setIsMenuOpen(false); }}>Contact</li>
            <li
              className={styles.navCta}
              onClick={() => { navigate('/pricing'); setIsMenuOpen(false); }}
            >
              Get Started
            </li>
          </ul>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroEyebrow}>About Full Trucking Service</div>
        <h1 className={styles.heroTitle}>
          Built by the Industry,<br />
          <span className={styles.heroAccent}>For the Industry.</span>
        </h1>
        <p className={styles.heroSub}>
          FTS is a complete trucking operations platform developed by Two Brothers Freight
          Solutions (2-BFS) — professionals who dispatched trucks, managed drivers, and
          felt every friction point first-hand. We built the software we wished existed.
        </p>
      </section>

      {/* ── MISSION ── */}
      <section className={styles.missionSection}>
        <div className={styles.missionGrid}>
          <div className={styles.missionCard}>
            <span className={styles.missionIcon}>🎯</span>
            <h3>Our Mission</h3>
            <p>
              To eliminate operational friction in the trucking industry by giving every company
              — from a single owner-operator to a 100-truck enterprise — a unified platform that
              manages loads, payroll, fuel, invoicing, compliance, and finances in real time.
            </p>
          </div>
          <div className={styles.missionCard}>
            <span className={styles.missionIcon}>🔭</span>
            <h3>Our Vision</h3>
            <p>
              To become the most trusted operating system in North American trucking — the platform
              that dispatchers open first every morning and that fleet managers rely on for every
              financial and operational decision they make.
            </p>
          </div>
          <div className={styles.missionCard}>
            <span className={styles.missionIcon}>🤝</span>
            <h3>Our Promise</h3>
            <p>
              Every feature in FTS was validated by active trucking professionals. We don't ship
              theoretical tools — we ship solutions that have been tested against real workflows,
              real frustrations, and real money on the line.
            </p>
          </div>
        </div>
      </section>

      {/* ── STORY ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>Our Story</div>
          <h2 className={styles.sectionTitle}>Why FTS Exists</h2>
        </div>
        <div className={styles.storyGrid}>
          <div className={styles.storyText}>
            <p>
              The trucking industry moves the economy — yet most companies are still managing
              operations through spreadsheets, disconnected apps, and paper logs. Dispatchers
              spend hours manually calculating driver pays. Accountants chase invoices across
              email threads. Fleet managers have no real-time view of where money is going.
            </p>
            <p>
              Two Brothers Freight Solutions built FTS to solve this. Not as a side project,
              but as the primary mission of a team that lived these problems every day running
              their own freight operations. We knew exactly what a dispatcher needs at 3am when
              a load falls through. We knew how painful it is to manually recreate a fuel report
              from paper receipts. We knew that generating a payroll for 10 drivers with different
              pay structures shouldn't take an entire day.
            </p>
            <p>
              So we built the platform that handles all of it — load management with AI auto-fill,
              driver payroll in seconds, fuel and IFTA reporting, invoicing and factoring,
              real-time financial dashboards, GPS tracking, a mobile driver app, and a balance
              sheet that shows you exactly where your business stands at any moment.
            </p>
            <p>
              FTS is not just software. It is the operational backbone that lets trucking companies
              focus on what matters: keeping trucks moving and delivering profit to the bottom line.
            </p>
          </div>
          <div className={styles.storyStats}>
            {[
              { val: "12+", label: "Core Modules" },
              { val: "500+", label: "Active Companies" },
              { val: "10K+", label: "Loads Managed" },
              { val: "99.8%", label: "Platform Uptime" },
            ].map((s, i) => (
              <div key={i} className={styles.storyStat}>
                <span className={styles.storyStatVal}>{s.val}</span>
                <span className={styles.storyStatLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>Our Journey</div>
          <h2 className={styles.sectionTitle}>From Idea to Full Platform</h2>
        </div>
        <div className={styles.timeline} ref={timelineRef}>
          {MILESTONES.map((m, i) => (
            <div
              key={i}
              className={`${styles.timelineItem} ${i <= visibleMilestone ? styles.timelineVisible : ''}`}
            >
              <div className={styles.timelineYear}>{m.year}</div>
              <div className={styles.timelineDot} />
              <div className={styles.timelineContent}>
                <h4>{m.title}</h4>
                <p>{m.desc}</p>
              </div>
            </div>
          ))}
          <div className={styles.timelineLine} />
        </div>
      </section>

      {/* ── CAPABILITIES ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>What FTS Does</div>
          <h2 className={styles.sectionTitle}>12 Modules. One Platform.</h2>
          <p className={styles.sectionSub}>
            Click any capability to learn more. Every module was built to solve a specific,
            real problem trucking companies face every day.
          </p>
        </div>
        <div className={styles.capGrid}>
          {CAPABILITIES.map((c, i) => (
            <div
              key={i}
              className={`${styles.capCard} ${capFilter === i ? styles.capCardOpen : ''}`}
              onClick={() => setCapFilter(capFilter === i ? null : i)}
            >
              <div className={styles.capHeader}>
                <span className={styles.capLabel}>{c.label}</span>
                <span className={styles.capArrow}>{capFilter === i ? '▲' : '▼'}</span>
              </div>
              {capFilter === i && (
                <p className={styles.capDesc}>{c.desc}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionEyebrow}>Our Values</div>
          <h2 className={styles.sectionTitle}>What Drives Every Decision We Make</h2>
        </div>
        <div className={styles.valuesGrid}>
          {VALUES.map((v, i) => (
            <div key={i} className={styles.valueCard} style={{ animationDelay: `${i * 0.1}s` }}>
              <span className={styles.valueIcon}>{v.icon}</span>
              <h3 className={styles.valueTitle}>{v.title}</h3>
              <p className={styles.valueDesc}>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW WE WORK ── */}
      <section className={styles.howSection}>
        <div className={styles.howInner}>
          <div className={styles.sectionEyebrow} style={{ textAlign: 'center', marginBottom: 14 }}>
            How We Build
          </div>
          <h2 className={styles.sectionTitle} style={{ textAlign: 'center', marginBottom: 36 }}>
            Our Commitments to Every User
          </h2>
          <ul className={styles.commitList}>
            {TEAM_VALUES.map((t, i) => (
              <li key={i} className={styles.commitItem}>
                <span className={styles.commitCheck}>✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>
            Ready to Run Your Fleet on FTS?
          </h2>
          <p className={styles.ctaSub}>
            Join hundreds of trucking companies that have replaced disconnected tools with
            one unified platform. Start your 30-day free trial — no credit card required.
          </p>
          <div className={styles.ctaBtns}>
            <button className={styles.btnPrimary} onClick={() => navigate('/pricing')}>
              View Plans & Pricing
            </button>
            <button className={styles.btnGhost} onClick={() => navigate('/contact')}>
              Talk to Us
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className={styles.footer}>
        <span className={styles.footerBrand}>© 2025 Full Trucking Service · 2-BFS Inc.</span>
        <div className={styles.footerLinks}>
          <a href="https://2-bfs.com/terms-and-conditions" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <a href="https://2-bfs.com/privacy-policy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <a href="https://2-bfs.com/panda-help" target="_blank" rel="noopener noreferrer">
            Help Center
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AboutUsPage;
